import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import MyLocationRounded from '@mui/icons-material/MyLocationRounded';
import PlaceRounded from '@mui/icons-material/PlaceRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import { Autocomplete, GoogleMap, Marker } from '@react-google-maps/api';
import { useForm } from 'react-hook-form';

import Button from './Button';
import { checkPermission, getCurrentPosition, requestLocationPermission } from '../platform/geolocation';
import { V2ApiError } from '../services/client';
import { readLocationCache } from '../services/locationCache';
import { useV2GoogleMaps } from '../state/GoogleMapsProvider';
import { v2Colors } from '../design/theme';
import { safeAreaInset } from '../platform/safeArea';

const DEFAULT_CENTER = { lat: 17.38714, lng: 78.491684 };

/** Below this, GPS jitter/refresh noise isn't worth flagging to the user. */
const DISTANCE_WARNING_THRESHOLD_KM = 0.3;

const searchInputStyle = {
  boxSizing: 'border-box',
  border: 'none',
  width: '100%',
  maxWidth: '100%',
  height: 44,
  padding: '0 40px 0 16px',
  borderRadius: 24,
  fontSize: 14,
  outline: 'none',
  background: '#fff',
  boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
};

/** Great-circle distance between two {lat,lng} points, in kilometres. */
function haversineKm(a, b) {
  if (!a || !b) return null;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

function formatDistance(km) {
  if (km == null || !Number.isFinite(km)) return '';
  if (km < 1) return `${Math.max(1, Math.round(km * 1000))} m`;
  return `${km.toFixed(1)} km`;
}

/**
 * Reverse-geocode confirmed pin via Google Maps Geocoder (already available with Maps JS + Places).
 * No extra npm dependency.
 * @returns {Promise<{ areaLabel: string, streetDetails: string, pincode: string }>}
 */
async function reverseGeocodeLatLng(latLng) {
  const empty = { areaLabel: '', streetDetails: '', pincode: '', formattedAddress: '' };
  if (typeof window === 'undefined' || !window.google?.maps?.Geocoder) return empty;

  return new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status !== 'OK' || !results?.[0]) {
        resolve(empty);
        return;
      }
      const best = results[0];
      const comps = best.address_components || [];
      const get = (type) => comps.find((c) => c.types.includes(type))?.long_name || '';

      const pincode = get('postal_code');
      const locality = get('sublocality_level_1') || get('sublocality') || get('neighborhood');
      const area = locality || get('locality') || get('administrative_area_level_3') || '';
      const route = get('route');
      const streetDetails = [route, locality || get('locality')].filter(Boolean).join(', ');

      resolve({
        areaLabel: area || best.formatted_address?.split(',')[0] || '',
        streetDetails: streetDetails || best.formatted_address || '',
        pincode: /^\d{6}$/.test(pincode) ? pincode : '',
        formattedAddress: best.formatted_address || '',
      });
    });
  });
}

function AddressFormFields({ register, errors }) {
  return (
    <>
      <TextField
        label="Full name"
        fullWidth
        {...register('userName', { required: 'Name is required' })}
        error={Boolean(errors.userName)}
        helperText={errors.userName?.message}
      />
      <TextField
        label="House / flat / door"
        fullWidth
        {...register('houseDetails', { required: 'Required' })}
        error={Boolean(errors.houseDetails)}
        helperText={errors.houseDetails?.message}
      />
      <TextField label="Street / area" fullWidth {...register('streetDetails')} />
      <TextField label="Landmark" fullWidth {...register('landmark')} />
      <TextField
        label="Pincode"
        fullWidth
        inputProps={{ maxLength: 6, inputMode: 'numeric' }}
        {...register('pincode', {
          required: 'Pincode required',
          pattern: { value: /^\d{6}$/, message: '6-digit pincode' },
        })}
        error={Boolean(errors.pincode)}
        helperText={errors.pincode?.message}
      />
    </>
  );
}

/**
 * Two-step add address (Swiggy / Licious style):
 * 1) Confirm delivery pin (GPS / search / drag) — lat/lng locked for riders
 * 2) Enter house / street / landmark details
 *
 * Parent must only mount when REACT_APP_MAP_KEY is set.
 * @param {{ onSave: (body: object) => Promise<void>, onCancel: () => void, onStepChange?: (step: 'pin'|'details') => void, mobile?: string }} props
 */
export function AddAddressFormMap({ onSave, onCancel, onStepChange, mobile }) {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: { userName: '', houseDetails: '', streetDetails: '', landmark: '', pincode: '' },
  });

  const [step, setStep] = useState('pin');
  const [latLong, setLatLong] = useState(DEFAULT_CENTER);
  /** Frozen when user confirms pin — used on save so details step cannot change rider coords. */
  const [confirmedLatLong, setConfirmedLatLong] = useState(null);
  const [areaLabel, setAreaLabel] = useState('');
  /** Same street address shown on the pin step — reused on the details step instead of raw lat/lng. */
  const [confirmedAddressLine, setConfirmedAddressLine] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [formError, setFormError] = useState('');
  const [locating, setLocating] = useState(false);
  /** Last known true GPS fix — kept separate from the (movable) pin so we can flag mismatches. */
  const [deviceLocation, setDeviceLocation] = useState(null);
  /** Live reverse-geocode of the pin, shown in the bottom sheet as the user searches / drags. */
  const [liveAddress, setLiveAddress] = useState({ areaLabel: '', formattedAddress: '', pincode: '' });
  const [geocoding, setGeocoding] = useState(false);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useV2GoogleMaps();

  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  const centerMap = useCallback((pos) => {
    if (!pos) return;
    mapRef.current?.setCenter(pos);
    mapRef.current?.setZoom(17);
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const cached = await readLocationCache();
      const cachedLat = Number(cached?.lat);
      const cachedLng = Number(cached?.lng);
      if (!cancelled && Number.isFinite(cachedLat) && Number.isFinite(cachedLng)) {
        const cachedPos = { lat: cachedLat, lng: cachedLng };
        setLatLong(cachedPos);
        setDeviceLocation(cachedPos);
        centerMap(cachedPos);
      }

      let perm = await checkPermission();
      if (perm === 'prompt') {
        perm = await requestLocationPermission();
      }
      if (perm !== 'granted' || cancelled) return;

      const pos = await getCurrentPosition();
      if (!cancelled && pos) {
        setLatLong(pos);
        setDeviceLocation(pos);
        centerMap(pos);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [centerMap]);

  const onMapLoad = useCallback(
    (mapInstance) => {
      mapRef.current = mapInstance;
      mapInstance.setCenter(latLong);
      mapInstance.setZoom(17);
    },
    [latLong]
  );

  useEffect(() => {
    if (step === 'pin') centerMap(latLong);
  }, [latLong, centerMap, step]);

  const useCurrentLocation = useCallback(async () => {
    setFormError('');
    setLocating(true);
    try {
      const perm = await requestLocationPermission();
      if (perm !== 'granted') {
        setFormError('Location permission is required. Enable it in settings or pin the map manually.');
        return;
      }
      const pos = await getCurrentPosition();
      if (!pos) {
        setFormError('Could not read your location. Try again or move the pin on the map.');
        return;
      }
      setLatLong(pos);
      setDeviceLocation(pos);
      centerMap(pos);
    } finally {
      setLocating(false);
    }
  }, [centerMap]);

  const distanceFromDeviceKm = haversineKm(deviceLocation, latLong);
  const showDistanceWarning =
    step === 'pin' && distanceFromDeviceKm != null && distanceFromDeviceKm >= DISTANCE_WARNING_THRESHOLD_KM;

  /** Live reverse-geocode of the pin (debounced) so the bottom sheet shows the resolved address as the user searches / drags. */
  useEffect(() => {
    if (step !== 'pin' || !isLoaded) return undefined;
    if (!Number.isFinite(latLong.lat) || !Number.isFinite(latLong.lng)) return undefined;

    let cancelled = false;
    setGeocoding(true);
    const timer = setTimeout(async () => {
      const geo = await reverseGeocodeLatLng(latLong);
      if (!cancelled) {
        setLiveAddress(geo);
        setGeocoding(false);
      }
    }, 450);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [latLong, step, isLoaded]);

  const onMarkerDragEnd = useCallback((e) => {
    if (!e?.latLng) return;
    setLatLong({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }, []);

  const onMapClick = useCallback((e) => {
    if (!e?.latLng) return;
    setLatLong({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }, []);

  const onAutoCompleteLoad = useCallback((ac) => {
    setSearchResult(ac);
  }, []);

  const onPlaceChanged = useCallback(() => {
    if (!searchResult) return;
    const place = searchResult.getPlace();
    const loc = place?.geometry?.location;
    if (!loc) {
      setFormError('Pick a place from the suggestions list.');
      return;
    }
    setLatLong({ lat: loc.lat(), lng: loc.lng() });
    setFormError('');
  }, [searchResult]);

  const confirmPin = useCallback(async () => {
    setFormError('');
    if (!isLoaded) {
      setFormError('Map is still loading. Please wait.');
      return;
    }
    if (!Number.isFinite(latLong.lat) || !Number.isFinite(latLong.lng)) {
      setFormError('Set a location on the map or search for a place.');
      return;
    }

    setConfirming(true);
    try {
      const geo = await reverseGeocodeLatLng(latLong);
      setConfirmedLatLong({ lat: latLong.lat, lng: latLong.lng });
      setAreaLabel(geo.areaLabel || '');
      setConfirmedAddressLine(geo.formattedAddress || geo.streetDetails || '');
      if (geo.streetDetails) {
        setValue('streetDetails', geo.streetDetails, { shouldDirty: true });
      }
      if (geo.pincode) {
        setValue('pincode', geo.pincode, { shouldDirty: true });
      }
      setStep('details');
    } finally {
      setConfirming(false);
    }
  }, [isLoaded, latLong, setValue]);

  const goBackToPin = useCallback(() => {
    setFormError('');
    setConfirmedLatLong(null);
    setStep('pin');
  }, []);

  const onSubmit = async (data) => {
    setFormError('');
    const pin = confirmedLatLong;
    if (!pin || !Number.isFinite(pin.lat) || !Number.isFinite(pin.lng)) {
      setFormError('Confirm your delivery location on the map first.');
      setStep('pin');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        userName: String(data.userName).replace(/'/g, ''),
        houseDetails: String(data.houseDetails).replace(/'/g, ''),
        streetDetails: String(data.streetDetails || '').replace(/'/g, ''),
        landmark: String(data.landmark || '').replace(/'/g, ''),
        pincode: String(data.pincode).trim(),
        latLong: { lat: pin.lat, lng: pin.lng },
      };
      await onSave(body);
    } catch (e) {
      setFormError(e instanceof V2ApiError ? e.message : 'Could not save address.');
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'pin') {
    if (!isLoaded) {
      return (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#fff',
          }}
        >
          {loadError ? (
            <Alert severity="error" sx={{ mx: 2 }}>
              Could not load Google Maps. Try again later.
            </Alert>
          ) : (
            <CircularProgress size={28} />
          )}
        </Box>
      );
    }

    return (
      <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={latLong}
            zoom={17}
            onLoad={onMapLoad}
            onClick={onMapClick}
            options={{
              mapTypeControl: false,
              fullscreenControl: false,
              streetViewControl: false,
              rotateControl: false,
              clickableIcons: false,
              minZoom: 8,
            }}
          >
            <Marker position={latLong} draggable onDragEnd={onMarkerDragEnd} />
          </GoogleMap>

          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              pt: `calc(20px + ${safeAreaInset.top})`,
              px: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              zIndex: 2,
            }}
          >
            <IconButton
              type="button"
              onClick={onCancel}
              aria-label="Back"
              sx={{ bgcolor: '#fff', boxShadow: '0 2px 10px rgba(0,0,0,0.18)', flexShrink: 0, '&:hover': { bgcolor: '#fafafa' } }}
            >
              <ArrowBackRounded />
            </IconButton>
            <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
              <Autocomplete
                onLoad={onAutoCompleteLoad}
                onPlaceChanged={onPlaceChanged}
                options={{
                  componentRestrictions: { country: 'in' },
                  fields: ['geometry', 'name', 'formatted_address'],
                }}
              >
                <input type="text" placeholder="Search for your area / society" style={searchInputStyle} />
              </Autocomplete>
              <SearchRounded
                fontSize="small"
                sx={{
                  position: 'absolute',
                  right: 14,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'text.secondary',
                  pointerEvents: 'none',
                }}
              />
            </Box>
          </Box>

          {formError ? (
            <Box sx={{ position: 'absolute', top: `calc(72px + ${safeAreaInset.top})`, left: 16, right: 16, zIndex: 2 }}>
              <Alert severity="error" onClose={() => setFormError('')}>
                {formError}
              </Alert>
            </Box>
          ) : null}

          <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 16, display: 'flex', justifyContent: 'center', zIndex: 2 }}>
            <Button
              type="button"
              variant="contained"
              size="small"
              startIcon={locating ? <CircularProgress size={14} color="inherit" /> : <MyLocationRounded />}
              disabled={locating}
              onClick={useCurrentLocation}
              sx={{
                borderRadius: '24px',
                bgcolor: '#fff',
                color: v2Colors.primary,
                boxShadow: 2,
                px: 2,
                '&:hover': { bgcolor: '#fafafa' },
              }}
            >
              {locating ? 'Locating…' : 'Use my current location'}
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            bgcolor: '#fff',
            borderRadius: '20px',
            boxShadow: '0 -6px 24px rgba(0,0,0,0.16)',
            mx: 2,
            mt: 1.5,
            mb: `calc(20px + ${safeAreaInset.bottom})`,
            px: 2,
            pt: 2,
            pb: 3,
            position: 'relative',
            zIndex: 3,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Place the pin at exact delivery location
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <PlaceRounded sx={{ color: v2Colors.primary, mt: 0.25 }} fontSize="small" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                {liveAddress.areaLabel || (geocoding ? 'Locating address…' : 'Delivery location')}
              </Typography>
              {liveAddress.formattedAddress ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  {liveAddress.formattedAddress}
                </Typography>
              ) : null}
            </Box>
          </Box>

          {showDistanceWarning ? (
            <Box sx={{ mt: 1.25, bgcolor: '#FDF1D6', color: '#8A6512', borderRadius: 1.5, px: 1.5, py: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                This is {formatDistance(distanceFromDeviceKm)} away from your current location.
              </Typography>
            </Box>
          ) : null}

          <Button
            type="button"
            fullWidth
            disabled={confirming}
            onClick={confirmPin}
            sx={{ borderRadius: '8px', mt: 2 }}
          >
            {confirming ? 'Confirming…' : 'Confirm & proceed'}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0, maxWidth: '100%' }}
    >
      {formError ? <Alert severity="error">{formError}</Alert> : null}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.25,
          p: 1.5,
          borderRadius: 2,
          bgcolor: v2Colors.canvas,
          border: '1px solid',
          borderColor: v2Colors.border,
        }}
      >
        <PlaceRounded sx={{ color: v2Colors.primary, mt: 0.25 }} fontSize="small" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {areaLabel || 'Delivery location'}
          </Typography>
          {confirmedAddressLine ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {confirmedAddressLine}
            </Typography>
          ) : null}
        </Box>
        <Button
          type="button"
          variant="text"
          size="small"
          onClick={goBackToPin}
          sx={{ flexShrink: 0, minWidth: 0, fontWeight: 700, color: v2Colors.primary }}
        >
          Change
        </Button>
      </Box>

      <Box>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ color: v2Colors.heading, mb: 1.25 }}
        >
          Receiver details
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          <TextField
            label="Full name"
            fullWidth
            {...register('userName', { required: 'Name is required' })}
            error={Boolean(errors.userName)}
            helperText={errors.userName?.message}
          />
          <TextField
            label="Phone number"
            fullWidth
            value={mobile || ''}
            InputProps={{ readOnly: true }}
            helperText="Linked to your account"
          />
        </Box>
      </Box>

      <Box>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ color: v2Colors.heading, mb: 1.25 }}
        >
          Address details
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          <TextField
            label="House / flat / door"
            fullWidth
            {...register('houseDetails', { required: 'Required' })}
            error={Boolean(errors.houseDetails)}
            helperText={errors.houseDetails?.message}
          />
          <TextField label="Street / area" fullWidth {...register('streetDetails')} />
          <TextField label="Landmark" fullWidth {...register('landmark')} />
          <TextField
            label="Pincode"
            fullWidth
            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
            {...register('pincode', {
              required: 'Pincode required',
              pattern: { value: /^\d{6}$/, message: '6-digit pincode' },
            })}
            error={Boolean(errors.pincode)}
            helperText={errors.pincode?.message}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button
          type="button"
          variant="outlined"
          fullWidth
          startIcon={<ArrowBackRounded />}
          onClick={goBackToPin}
          sx={{ borderRadius: '5px' }}
        >
          Back
        </Button>
        <Button type="submit" fullWidth disabled={submitting} sx={{ borderRadius: '5px' }}>
          {submitting ? 'Saving…' : 'Save address'}
        </Button>
      </Box>
    </Box>
  );
}

/** Fallback when REACT_APP_MAP_KEY is unset — manual coordinates. */
export function AddAddressFormManual({ onSave, onCancel }) {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      userName: '',
      houseDetails: '',
      streetDetails: '',
      landmark: '',
      pincode: '',
      lat: String(DEFAULT_CENTER.lat),
      lng: String(DEFAULT_CENTER.lng),
    },
  });

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [locating, setLocating] = useState(false);

  const useCurrentLocation = useCallback(async () => {
    setFormError('');
    setLocating(true);
    try {
      const perm = await requestLocationPermission();
      if (perm !== 'granted') {
        setFormError('Location permission is required.');
        return;
      }
      const pos = await getCurrentPosition();
      if (!pos) {
        setFormError('Could not read your location.');
        return;
      }
      setValue('lat', String(pos.lat), { shouldValidate: true });
      setValue('lng', String(pos.lng), { shouldValidate: true });
    } finally {
      setLocating(false);
    }
  }, [setValue]);

  const onSubmit = async (data) => {
    setFormError('');
    const lat = Number(data.lat);
    const lng = Number(data.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setFormError('Enter valid latitude and longitude.');
      return;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setFormError('Coordinates out of range.');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        userName: String(data.userName).replace(/'/g, ''),
        houseDetails: String(data.houseDetails).replace(/'/g, ''),
        streetDetails: String(data.streetDetails || '').replace(/'/g, ''),
        landmark: String(data.landmark || '').replace(/'/g, ''),
        pincode: String(data.pincode).trim(),
        latLong: { lat, lng },
      };
      await onSave(body);
    } catch (e) {
      setFormError(e instanceof V2ApiError ? e.message : 'Could not save address.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {formError ? <Alert severity="error">{formError}</Alert> : null}
      <Alert severity="info">
        Set latitude and longitude for the delivery pin (e.g. from Google Maps). Add REACT_APP_MAP_KEY for map search.
      </Alert>
      <Button
        type="button"
        variant="outlined"
        size="small"
        startIcon={locating ? <CircularProgress size={14} color="inherit" /> : <MyLocationRounded />}
        disabled={locating}
        onClick={useCurrentLocation}
        sx={{ borderRadius: '5px', alignSelf: 'flex-start' }}
      >
        {locating ? 'Locating…' : 'Use current location'}
      </Button>
      <TextField
        label="Latitude"
        fullWidth
        {...register('lat', { required: 'Required' })}
        error={Boolean(errors.lat)}
        helperText={errors.lat?.message}
      />
      <TextField
        label="Longitude"
        fullWidth
        {...register('lng', { required: 'Required' })}
        error={Boolean(errors.lng)}
        helperText={errors.lng?.message}
      />
      <AddressFormFields register={register} errors={errors} />
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button type="button" variant="outlined" fullWidth onClick={onCancel} sx={{ borderRadius: '5px' }}>
          Cancel
        </Button>
        <Button type="submit" fullWidth disabled={submitting} sx={{ borderRadius: '5px' }}>
          {submitting ? 'Saving…' : 'Save address'}
        </Button>
      </Box>
    </Box>
  );
}
